// src/pages/settings/UserPreferencesTab.tsx
//
// S4 (ClickUp 86d42twq8) — converted from the standalone `/settings` route
// (`UserPreferencesPage.tsx`) into the settings drawer's MY ACCOUNT tab. `MainLayout` and
// `PageContainer` are stripped; the header actions `PageContainer` used to render (Reset
// to Default / Save Changes) move onto the wrapping `SectionCard`'s own `title`/`extra`,
// the same conversion every other tab in this drawer already went through (see
// `CustomTestPackages.tsx` for the pattern).
//
// This is the only tab a patient will ever see — its route was bare `PrivateRoute`, so
// `visible: () => true` in `settingsTabs.tsx` reproduces that exactly.
import React, { useState, useEffect } from "react";
import { Tabs, Select, InputNumber, Segmented, Alert, Row, Col, Space, Typography, Button } from "antd";
import { DesktopOutlined, BellOutlined, SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import { loadUserPreferences, UserPreferences, DEFAULT_PREFERENCES } from "../../utils/userPrefs/UserPrefs";
import { parsePreferences } from "../../utils/userPrefs/parsePreferences";
import { updateProfile } from "../../api/admin/userApi";
import Cookies from "js-cookie";
import { getSessionUser } from "../../utils/auth/sessionUser";
import ThemeSwitcher from "./themes/ThemeSwitcher";
import NotificationPreferencesTab from "./settings/NotificationPreferencesTab";
import { User } from "../../types/admin/UserTypes";
import { SectionCard } from "../../components/shared";
import { useNotifications } from "../../context/NotificationContext";

const { Text } = Typography;

const UserPreferencesTab: React.FC = () => {
	const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
	const [activeTab, setActiveTab] = useState("interface");
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const [currentUser, setCurrentUser] = useState<User | null>(null);
	const { showSuccess, showError } = useNotifications();

	// Date format options
	const dateFormatOptions = [
		{ label: "DD/MM/YYYY", value: "dd/mm/yy" },
		{ label: "MM/DD/YYYY", value: "mm/dd/yy" },
		{ label: "YYYY-MM-DD", value: "yy-mm-dd" },
	];

	// Time format options
	const timeFormatOptions = [
		{ label: "12-Hour", value: "12" },
		{ label: "24-Hour", value: "24" },
	];

	// Xoe assistant surface options — see XoeChatMode in UserPrefs.ts.
	// "Both" means both entry points to ONE chat, not two independent chats.
	const xoeChatModeOptions = [
		{ label: "Floating", value: "floating" },
		{ label: "Full Page", value: "fullpage" },
		{ label: "Both", value: "both" },
	];
	// Load preferences and user data on component mount
	useEffect(() => {
		const userPrefs = loadUserPreferences();
		setPreferences(userPrefs);

		// Load current user data
		try {
			const user = getSessionUser();
			setCurrentUser(user);
		} catch (error) {
			console.error("Error parsing user data:", error);
		}
	}, []);

	// Update preference and mark as unsaved
	const updatePreference = (key: keyof UserPreferences, value: any) => {
		setPreferences((prev) => ({
			...prev,
			[key]: value,
		}));
		setUnsavedChanges(true);
	};

	// Save all preferences
	const saveAllPreferences = async () => {
		try {
			// Convert preferences object to JSON string
			const preferencesJson = JSON.stringify(preferences);
			let user = getSessionUser();
			user.preferences = preferencesJson;

			// Save to API
			const newUser: User = await updateProfile(user);

			// Update cookies with new preferences
			Cookies.remove("userPreferences");

			const savedPreferences = parsePreferences(newUser.preferences) ?? preferences;

			// Set the preferences cookie
			Cookies.set("userPreferences", JSON.stringify(savedPreferences), { expires: 365 });

			// Apply theme changes immediately without page reload
			const { applyThemeClasses } = await import("../../utils/userPrefs/themeUtils");
			applyThemeClasses(preferences.theme as 'light' | 'dark');

			// Dispatch custom event to trigger theme reload in App.tsx
			window.dispatchEvent(new CustomEvent('themeChange', {
				detail: preferences
			}));

			showSuccess("Preferences saved successfully");
			setUnsavedChanges(false);

		} catch (error) {
			console.error("Error saving preferences:", error);
			showError("Failed to save preferences");
		}
	};

	// Reset preferences to default
	const resetToDefault = () => {
		setPreferences(DEFAULT_PREFERENCES);
		setUnsavedChanges(true);
		showSuccess("Preferences reset to default. Click 'Save Changes' to apply.");
	};

	// Tab items configuration
	const getTabItems = () => {
		const items = [
			{
				key: "notifications",
				label: (
					<span>
						<BellOutlined /> Notifications
					</span>
				),
				children: (
					<div>
						{currentUser && currentUser.id ? (
							<NotificationPreferencesTab userId={currentUser.id} />
						) : (
							<Alert message="Unable to load user information" description="Please refresh the page to try again." type="warning" showIcon />
						)}
					</div>
				),
			},
			{
				key: "interface",
				label: (
					<span>
						<DesktopOutlined /> Interface
					</span>
				),
				children: (
					<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
						<SectionCard title="Theme Preferences">
							<ThemeSwitcher preferences={preferences} updatePreference={updatePreference} />
						</SectionCard>

						<SectionCard title="Display Preferences">
							<Row gutter={[24, 16]}>
								<Col xs={24} md={12} lg={8}>
									<Space direction="vertical" style={{ width: "100%" }}>
										<Text strong>Table Rows Per Page</Text>
										<InputNumber value={preferences.tableRowsPerPage} onChange={(value) => updatePreference("tableRowsPerPage", value || 10)} min={5} max={100} style={{ width: "100%" }} />
									</Space>
								</Col>

								<Col xs={24} md={12} lg={8}>
									<Space direction="vertical" style={{ width: "100%" }}>
										<Text strong>Date Format</Text>
										<Select value={preferences.dateFormat} options={dateFormatOptions} onChange={(value) => updatePreference("dateFormat", value)} style={{ width: "100%" }} />
									</Space>
								</Col>

								<Col xs={24} md={12} lg={8}>
									<Space direction="vertical" style={{ width: "100%" }}>
										<Text strong>Time Format</Text>
										<Segmented value={preferences.timeFormat} options={timeFormatOptions} onChange={(value) => updatePreference("timeFormat", value)} style={{ width: "100%" }} />
									</Space>
								</Col>

								<Col xs={24} md={12} lg={8}>
									<Space direction="vertical" style={{ width: "100%" }}>
										<Text strong>Xoe Assistant</Text>
										<Segmented value={preferences.xoeChatMode} options={xoeChatModeOptions} onChange={(value) => updatePreference("xoeChatMode", value)} style={{ width: "100%" }} data-testid="preferences-xoe-chat-mode" />
									</Space>
								</Col>
							</Row>
						</SectionCard>
					</div>
				),
			},
		];

		return items;
	};

	const tabItems = getTabItems();

	return (
		<SectionCard
			title="User Preferences"
			subtitle="Customise your interface, notifications and email settings"
			extra={
				<Space>
					<Button icon={<ReloadOutlined />} onClick={resetToDefault}>
						Reset to Default
					</Button>
					<Button type="primary" icon={<SaveOutlined />} onClick={saveAllPreferences}>
						Save Changes
					</Button>
				</Space>
			}
		>
			{unsavedChanges && (
				<Alert
					message="You have unsaved changes"
					description="Click 'Save Changes' to apply your preferences."
					type="info"
					showIcon
					closable
					style={{ marginBottom: 16 }}
				/>
			)}

			<Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
		</SectionCard>
	);
};

export default UserPreferencesTab;
